import { createClient, createConfig } from './generated/client';
import * as generated from './generated/sdk.gen';
import { interceptors, type Singleton } from './auth';
import type { ApiError } from './errors';

type SDK = typeof generated;

/**
 * A list of URLs that should be ignored by the authentication interceptor by default.
 */
const AUTH_IGNORED_URLS: string[] = [
  '/accounts/get-token',
  '/accounts/refresh-token',
  '/accounts/register',
  '/accounts/password/forgot',
  '/accounts/password/reset/confirm',
  '/accounts/verify-email/',
  '/schema/',
];

/**
 * No-op function used as default for optional callbacks.
 */
const noop = (err?: ApiError) => {
  if (err) {
    console.error('Unhandled refresh error:', err);
  }
};

/**
 * Options for initializing the API client.
 */
interface Options {
  /**
   * The base URL for the API. If not provided, it defaults to the VITE_API_URL environment variable.
   */
  url?: string;

  /**
   * The authentication token to use.
   */
  token?: Singleton<string>;

  /**
   * The API key to use for authentication.
   */
  api_key?: Singleton<string>;

  /**
   * Callback function called when token refresh fails.
   */
  on_refresh_failed?: (err?: ApiError) => void;
}

/**
 * Initializes the API client with optional URL and authentication parameters.
 * Sets up automatic token refresh for bearer tokens while maintaining API key functionality.
 *
 * @param options - Configuration options for the API client
 * @returns An object containing the SDK methods
 */
const api = (options: Options = {}) => {
  const { url, token, api_key, on_refresh_failed = noop } = options;

  const client = createClient(createConfig({ baseUrl: url }));

  const ignored_urls = AUTH_IGNORED_URLS;

  if (token) {
    interceptors.request({
      token,
      api_key,
      ignored_urls,
      on_refresh_failed,
    })(client);

    interceptors.response({
      token,
      on_refresh_failed,
      ignored_urls,
    })(client);
  }

  if (api_key) {
    interceptors.request({
      token,
      api_key,
      ignored_urls,
      on_refresh_failed,
    })(client);
  }

  // 3. Wrap each SDK method to inject the configured client instance.
  // This uses a reducer to build a new SDK object where each method is
  // pre-configured with the client created above.
  const sdk = Object.keys(generated).reduce((acc, key) => {
    const fn = generated[key as keyof SDK];

    // Create a new function that calls the original SDK method,
    // but automatically injects the configured `client` instance.
    (acc as any)[key] = (options: any) => fn({ ...options, client });

    return acc;
  }, {} as SDK);

  return sdk;
};

export { api, type SDK, type Singleton };
export { AuthSingleton, jwt } from './auth';
export { check, formatError, type ApiError, type ErrorDetail } from './errors';
export * from './generated/types.gen';
